import {
  IPlatformLead,
  IPlatformLeadBulkUpdateDto,
  IPlatformLeadLogDto,
  IPlatformLeadWithStats,
  IPlatformFeaturePopularity,
  IPlatformPipelineSummary,
  PlatformLeadStageEnum,
  PlatformLeadActivityTypeEnum,
  PlatformLeadSourceEnum,
  PlatformLeadPriorityEnum,
} from '@torim-app/types';
import { platformUrlBuilder, platformFetch } from './utils';

export interface ILeadListQuery {
  stage?: PlatformLeadStageEnum;
  stages?: PlatformLeadStageEnum[];
  feature?: string;
  days?: number;
  source?: PlatformLeadSourceEnum;
  priority?: PlatformLeadPriorityEnum;
  assignedTo?: string;
  search?: string;
  onlyManual?: boolean;
  onlyStale?: boolean;
  onlyNeverContacted?: boolean;
  withStats?: boolean;
  limit?: number;
  skip?: number;
}

const LEADS_PAGE_SIZE = 500;

const toParams = (query?: ILeadListQuery): Record<string, string | number> => {
  const params: Record<string, string | number> = {};
  if (!query) return params;
  if (query.stage) params.stage = query.stage;
  if (query.stages?.length) params.stages = query.stages.join(',');
  if (query.feature) params.feature = query.feature;
  if (query.days) params.days = query.days;
  if (query.source) params.source = query.source;
  if (query.priority) params.priority = query.priority;
  if (query.assignedTo) params.assignedTo = query.assignedTo;
  if (query.search) params.search = query.search;
  if (query.onlyManual) params.onlyManual = 'true';
  if (query.onlyStale) params.onlyStale = 'true';
  if (query.onlyNeverContacted) params.onlyNeverContacted = 'true';
  if (query.withStats) params.withStats = 'true';
  if (query.limit) params.limit = query.limit;
  if (query.skip) params.skip = query.skip;
  return params;
};

export class CrmApi {
  static async fetchLeads(query?: ILeadListQuery): Promise<IPlatformLeadWithStats[]> {
    const url = platformUrlBuilder(toParams(query), 'leads');
    const response = await platformFetch(url);
    if (!response.ok) throw 'Failed to fetch leads';
    return response.json();
  }

  /** Fetches all leads in pages so a single request never transfers the whole collection */
  static async fetchAllLeads(
    query?: Omit<ILeadListQuery, 'limit' | 'skip'>,
  ): Promise<IPlatformLeadWithStats[]> {
    const all: IPlatformLeadWithStats[] = [];
    let skip = 0;
    for (;;) {
      const page = await this.fetchLeads({ ...query, limit: LEADS_PAGE_SIZE, skip });
      all.push(...page);
      if (page.length < LEADS_PAGE_SIZE) break;
      skip += LEADS_PAGE_SIZE;
    }
    return all;
  }

  static async fetchLeadById(id: string): Promise<IPlatformLead> {
    const url = platformUrlBuilder({}, `leads/${id}`);
    const response = await platformFetch(url);
    if (!response.ok) throw 'Failed to fetch lead';
    return response.json();
  }

  static async fetchPopularFeatures(): Promise<IPlatformFeaturePopularity[]> {
    const url = platformUrlBuilder({}, 'leads/features');
    const response = await platformFetch(url);
    if (!response.ok) throw 'Failed to fetch popular features';
    return response.json();
  }

  static async fetchPipelineSummary(): Promise<IPlatformPipelineSummary> {
    const url = platformUrlBuilder({}, 'leads/pipeline-summary');
    const response = await platformFetch(url);
    if (!response.ok) throw 'Failed to fetch pipeline summary';
    return response.json();
  }

  static async fetchDuplicates(): Promise<{ phone: string; leads: IPlatformLead[] }[]> {
    const url = platformUrlBuilder({}, 'leads/duplicates');
    const response = await platformFetch(url);
    if (!response.ok) throw 'Failed to fetch duplicates';
    return response.json();
  }

  static async createLead(data: Partial<IPlatformLead>): Promise<IPlatformLead> {
    const url = platformUrlBuilder({}, 'leads');
    const response = await platformFetch(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw err?.message || 'Failed to create lead';
    }
    return response.json();
  }

  static async updateLead(id: string, data: Partial<IPlatformLead>): Promise<IPlatformLead> {
    const url = platformUrlBuilder({}, `leads/${id}`);
    const response = await platformFetch(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!response.ok) throw 'Failed to update lead';
    return response.json();
  }

  /**
   * Saves a whole conversation at once — note, status, requested features and a
   * follow-up task — so logging a call costs one request.
   */
  static async logInteraction(
    id: string,
    dto: IPlatformLeadLogDto,
  ): Promise<IPlatformLeadWithStats> {
    const url = platformUrlBuilder({}, `leads/${id}/log`);
    const response = await platformFetch(url, {
      method: 'POST',
      body: JSON.stringify(dto),
    });
    if (!response.ok) throw 'Failed to log interaction';
    return response.json();
  }

  static async bulkUpdate(dto: IPlatformLeadBulkUpdateDto): Promise<{ updated: number }> {
    const url = platformUrlBuilder({}, 'leads/bulk');
    const response = await platformFetch(url, {
      method: 'POST',
      body: JSON.stringify(dto),
    });
    if (!response.ok) throw 'Failed to update leads';
    return response.json();
  }

  static async deleteLead(id: string): Promise<{ success: true }> {
    const url = platformUrlBuilder({}, `leads/${id}`);
    const response = await platformFetch(url, { method: 'DELETE' });
    if (!response.ok) throw 'Failed to delete lead';
    return response.json();
  }

  static async mergeLeads(targetId: string, sourceId: string): Promise<IPlatformLead> {
    const url = platformUrlBuilder({}, `leads/${targetId}/merge/${sourceId}`);
    const response = await platformFetch(url, { method: 'POST' });
    if (!response.ok) throw 'Failed to merge leads';
    return response.json();
  }

  static async addNote(
    id: string,
    text: string,
    type: PlatformLeadActivityTypeEnum = PlatformLeadActivityTypeEnum.Note,
    author?: string,
  ): Promise<IPlatformLead> {
    const url = platformUrlBuilder({}, `leads/${id}/notes`);
    const response = await platformFetch(url, {
      method: 'POST',
      body: JSON.stringify({ text, type, author }),
    });
    if (!response.ok) throw 'Failed to add note';
    return response.json();
  }

  static async linkBusiness(id: string, bid: string): Promise<IPlatformLead> {
    const url = platformUrlBuilder({}, `leads/${id}/link-business`);
    const response = await platformFetch(url, {
      method: 'POST',
      body: JSON.stringify({ bid }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw err?.message || 'Failed to link business';
    }
    return response.json();
  }

  /** Imports registered businesses as leads. Without `days`, the whole history. */
  static async syncLeads(days?: number): Promise<{ created: number; total: number }> {
    const url = platformUrlBuilder(days ? { days } : {}, 'leads/sync');
    const response = await platformFetch(url, { method: 'POST' });
    if (!response.ok) throw 'Failed to sync leads';
    return response.json();
  }

  static async fetchMe(): Promise<{ userId: string; email: string }> {
    const url = platformUrlBuilder({}, 'me');
    const response = await platformFetch(url);
    if (!response.ok) throw 'Failed to fetch current admin';
    return response.json();
  }
}
